import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, ArrowUpRight, Sparkle } from "@phosphor-icons/react";

// Bump this key when you want the announcement to re-appear for returning visitors.
// e.g. change to "ot_move_v2" if you tweak the content and want everyone to see it again.
const STORAGE_KEY = "ot_move_v1";

// Google Maps direction to the new HQ
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Tollaan+75,+1932+Zaventem,+Belgium";

export default function MoveAnnouncement() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only show if the visitor hasn't dismissed it before
    let seen = false;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      /* localStorage disabled — show anyway */
    }
    if (!seen) {
      // Small delay so it feels intentional, not jarring
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-ot-fg/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="move-announcement-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-ot-bg border-2 border-ot-fg shadow-[14px_14px_0px_var(--ot-indigo)]"
            data-testid="move-announcement-modal"
            role="dialog"
            aria-labelledby="move-announcement-title"
            aria-modal="true"
          >
            {/* Close button */}
            <button
              onClick={close}
              aria-label="Close announcement"
              className="absolute -top-4 -right-4 w-11 h-11 bg-ot-coral border-2 border-ot-fg flex items-center justify-center hover:rotate-90 transition-transform shadow-[4px_4px_0px_var(--ot-fg)]"
              data-testid="move-announcement-close"
            >
              <X size={20} weight="bold" />
            </button>

            {/* Top ribbon */}
            <div className="bg-ot-peach border-b-2 border-ot-fg px-6 py-3 flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-ot-indigo" />
              <span className="text-xs uppercase tracking-widest font-bold">
                We've moved · New address
              </span>
            </div>

            {/* Body */}
            <div className="px-6 md:px-8 py-8">
              <motion.h2
                id="move-announcement-title"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.35 }}
                className="font-display text-3xl sm:text-4xl uppercase tracking-tighter leading-[0.95]"
              >
                Same team.
                <br />
                <span className="bg-ot-mint border-2 border-ot-fg px-2">Brand-new HQ.</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22, duration: 0.35 }}
                className="mt-5 text-base text-ot-fg/75"
              >
                OneTec has moved to a fresh space in Zaventem. Everything
                you love stays exactly the same — phone number, email,
                team, projects — only the postbox changed.
              </motion.p>

              {/* Address card */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.35 }}
                className="mt-6 bg-white border-2 border-ot-fg p-5"
              >
                <p className="text-[11px] uppercase tracking-widest font-bold text-ot-indigo mb-3">
                  Come visit us
                </p>
                <div className="flex items-start gap-3">
                  <MapPin
                    size={22}
                    weight="bold"
                    className="mt-0.5 shrink-0 text-ot-fg"
                  />
                  <div className="text-base leading-snug">
                    <p className="font-display uppercase tracking-tight text-lg">
                      OneTec SRL
                    </p>
                    <p className="mt-1">
                      Tollaan 75
                      <br />
                      1932 Zaventem — Belgium
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Actions */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.35 }}
                className="mt-7 flex flex-col sm:flex-row gap-3"
              >
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nb-btn flex-1 justify-center"
                  data-testid="move-announcement-directions"
                >
                  Get directions <ArrowUpRight weight="bold" />
                </a>
                <button
                  onClick={close}
                  className="nb-btn nb-btn-ghost flex-1 justify-center"
                  data-testid="move-announcement-dismiss"
                >
                  Got it, thanks
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
